'use client'

/**
 * The Command Briefing feed (plan 03 CHG-13), read once for the whole shell.
 *
 * Three surfaces show the same payload, so they share one poll instead of three:
 * the Command Briefing hero and "Today" cards, the header bell
 * (NotificationCenter), and the chips of the Ask panel and the AskBar
 * (`briefing_suggested_prompts`). The poll interval comes from the payload
 * (`briefing_poll_seconds`), runs only while the tab is visible, and only for
 * roles that may read `/api/dashboard/briefing` (an anonymous call would send the
 * sign-in page into a Keycloak redirect).
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useSession } from 'next-auth/react'
import { apiClient } from '@/lib/api-client'
import type { Briefing } from '@/lib/briefing'

/** Roles allowed by the /api/dashboard.* authz entry that use this shell. */
const BRIEFING_READER_ROLES = ['admin', 'user', 'logistics_leader']
/** Used until the first payload says otherwise. */
const DEFAULT_POLL_SECONDS = 10
/** After this many failures in a row the poll stops until the next refresh() (backend down, 403). */
const MAX_FAILURES = 3

interface BriefingState {
  briefing: Briefing | null
  loading: boolean
  error: boolean
  refresh: () => void
}

const BriefingContext = createContext<BriefingState>({
  briefing: null,
  loading: false,
  error: false,
  refresh: () => undefined,
})

export function useBriefing(): BriefingState {
  return useContext(BriefingContext)
}

export function BriefingProvider({ children }: { children: React.ReactNode }) {
  const { data: session, status } = useSession()
  const canRead =
    status === 'authenticated' && (session?.roles ?? []).some((role) => BRIEFING_READER_ROLES.includes(role))
  const [briefing, setBriefing] = useState<Briefing | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [generation, setGeneration] = useState(0)
  const failures = useRef(0)

  const refresh = useCallback(() => {
    failures.current = 0
    setGeneration((n) => n + 1)
  }, [])

  useEffect(() => {
    if (!canRead) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined

    const load = async () => {
      if (document.visibilityState === 'hidden') {
        timer = setTimeout(load, DEFAULT_POLL_SECONDS * 1000)
        return
      }
      setLoading((was) => was || briefing === null)
      try {
        const next = await apiClient.get<Briefing>('/api/dashboard/briefing')
        if (cancelled) return
        failures.current = 0
        setBriefing(next)
        setError(false)
        timer = setTimeout(load, Math.max(3, next.poll_seconds || DEFAULT_POLL_SECONDS) * 1000)
      } catch {
        if (cancelled) return
        failures.current += 1
        setError(true)
        if (failures.current < MAX_FAILURES) timer = setTimeout(load, DEFAULT_POLL_SECONDS * 1000)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
    // `briefing` is read only for the first-load spinner; re-running on it would double the poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead, generation])

  return (
    <BriefingContext.Provider value={{ briefing: canRead ? briefing : null, loading, error, refresh }}>
      {children}
    </BriefingContext.Provider>
  )
}
